import React from 'react';
import { PowerOff, Wifi, WifiOff } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

export const formatRupiah = (n) => `Rp ${new Intl.NumberFormat('id-ID').format(Math.round(Number(n) || 0))}`;
const formatDate = (d) => (d ? new Date(d).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' }) : '-');
export const formatAge = (days) => {
  const d = Number(days) || 0;
  const m = Math.floor(d / 30);
  const r = d % 30;
  return m ? `${m} bln${r ? ` ${r} hr` : ''}` : `${r} hr`;
};
const ageTone = (days) => (days >= 180
  ? 'bg-rose-600 text-white border-rose-600'
  : days >= 90 ? 'bg-rose-100 text-rose-700 border-rose-300 dark:bg-rose-900/40 dark:text-rose-300'
  : 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/40 dark:text-amber-300');

const Action = ({ c, canStop, onStop, prefix }) => {
  if (!c.is_active) {
    return <Badge data-testid={`${prefix}stopped-badge-${c.id}`} className="bg-slate-200 text-slate-700 border border-slate-300 dark:bg-slate-700 dark:text-slate-200">Berhenti</Badge>;
  }
  if (!canStop) return <span className="text-xs text-slate-400">-</span>;
  return (
    <Button size="sm" variant="outline" onClick={() => onStop(c)} data-testid={`${prefix}stop-customer-btn-${c.id}`}
      className="h-8 border-rose-300 text-rose-700 hover:bg-rose-50 hover:text-rose-800 dark:border-rose-800 dark:text-rose-300 dark:hover:bg-rose-950">
      <PowerOff className="w-3.5 h-3.5 mr-1.5" /> Putuskan
    </Button>
  );
};

const Online = ({ c }) => (
  <span className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
    {c.is_online ? <Wifi className="w-3.5 h-3.5 text-emerald-600" /> : <WifiOff className="w-3.5 h-3.5" />}
    {c.is_online ? 'Online' : formatDate(c.last_online)}
  </span>
);

const NameCell = ({ c, onOpen, prefix }) => (
  <div className="min-w-0">
    <button type="button" onClick={() => onOpen(c.id)} data-testid={`${prefix}stopped-customer-name-${c.id}`}
      className="font-semibold text-slate-900 dark:text-slate-100 hover:text-indigo-600 dark:hover:text-indigo-400 text-left truncate max-w-full">
      {c.name}
    </button>
    <div className="text-[11px] text-slate-500 dark:text-slate-400 font-mono truncate">{c.account_number} · {c.pppoe_username || '-'}</div>
  </div>
);

const AgeBadge = ({ c, prefix }) => (
  <Badge data-testid={`${prefix}stopped-age-${c.id}`} className={`border tabular-nums ${ageTone(Number(c.overdue_days))}`}>{formatAge(c.overdue_days)}</Badge>
);

const DesktopTable = ({ rows, canStop, onStop, onOpen }) => (
  <div className="hidden md:block overflow-x-auto" data-testid="stopped-customers-table">
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
          <th className="py-2.5 pr-3 font-semibold">Pelanggan</th>
          <th className="py-2.5 pr-3 font-semibold">Cabang · Paket</th>
          <th className="py-2.5 pr-3 font-semibold">Tagihan Tertua</th>
          <th className="py-2.5 pr-3 font-semibold">Lama Isolir</th>
          <th className="py-2.5 pr-3 font-semibold text-right">Tunggakan</th>
          <th className="py-2.5 pr-3 font-semibold">Terakhir Online</th>
          <th className="py-2.5 font-semibold text-right">Aksi</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((c) => (
          <tr key={c.id} data-testid={`stopped-row-${c.id}`} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors">
            <td className="py-2.5 pr-3 max-w-[260px]"><NameCell c={c} onOpen={onOpen} prefix="" /></td>
            <td className="py-2.5 pr-3 text-xs text-slate-600 dark:text-slate-300">
              <div className="truncate max-w-[200px]">{c.branch_name || '-'}</div>
              <div className="truncate max-w-[200px] text-slate-400">{c.service_plan_name || '-'}</div>
            </td>
            <td className="py-2.5 pr-3 text-xs text-slate-600 dark:text-slate-300 whitespace-nowrap">{formatDate(c.oldest_due)}</td>
            <td className="py-2.5 pr-3"><AgeBadge c={c} prefix="" /></td>
            <td className="py-2.5 pr-3 text-right whitespace-nowrap">
              <div className="font-semibold tabular-nums text-slate-900 dark:text-slate-100" data-testid={`stopped-outstanding-${c.id}`}>{formatRupiah(c.unpaid_total)}</div>
              <div className="text-[11px] text-slate-400">{c.unpaid_count} tagihan</div>
            </td>
            <td className="py-2.5 pr-3 whitespace-nowrap"><Online c={c} /></td>
            <td className="py-2.5 text-right"><Action c={c} canStop={canStop} onStop={onStop} prefix="" /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const MobileCards = ({ rows, canStop, onStop, onOpen }) => (
  <div className="md:hidden space-y-2" data-testid="stopped-customers-cards">
    {rows.map((c) => (
      <div key={c.id} data-testid={`mobile-stopped-row-${c.id}`} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <NameCell c={c} onOpen={onOpen} prefix="mobile-" />
          <AgeBadge c={c} prefix="mobile-" />
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div><div className="text-slate-400">Tagihan tertua</div><div className="text-slate-700 dark:text-slate-200">{formatDate(c.oldest_due)}</div></div>
          <div className="text-right"><div className="text-slate-400">{c.unpaid_count} tagihan</div><div className="font-semibold tabular-nums text-slate-900 dark:text-slate-100">{formatRupiah(c.unpaid_total)}</div></div>
        </div>
        <div className="flex items-center justify-between gap-2">
          <Online c={c} />
          <Action c={c} canStop={canStop} onStop={onStop} prefix="mobile-" />
        </div>
      </div>
    ))}
  </div>
);

export default function StoppedCustomersList(props) {
  return (
    <>
      <DesktopTable {...props} />
      <MobileCards {...props} />
    </>
  );
}
